import React, { useState, type ComponentProps } from 'react';
import { DisconnectOutlined, LinkOutlined } from '@ant-design/icons';
import AssignmentCell, { PlatformAssignment } from './DesignPresetAssignment';
import { getPresetSpacingAssignment } from './design-preset-spacing';
import type { PresetAssignmentChange } from './design-preset-editing';
import css from './DesignPresetsEditor.scss';

type Props = Omit<ComponentProps<typeof PlatformAssignment>, 'property' | 'onChange' | 'assignmentLabel'> & {
	properties: Parameters<typeof getPresetSpacingAssignment>[0]['properties'];
	onChange: (changes: readonly PresetAssignmentChange[]) => void;
};

export default function DesignPresetSpacingControl({ properties, onChange, ...context }: Props) {
	const shared = getPresetSpacingAssignment({ properties, ...context });
	const [allSides, setAllSides] = useState(!shared.mixed);
	return (
		<>
			<div className={css.spaceMode} role="group" aria-label="Spacing sides">
				<button
					type="button"
					disabled={context.disabled}
					aria-pressed={allSides}
					onClick={() => setAllSides(true)}
				>
					<LinkOutlined /> All sides
				</button>
				<button
					type="button"
					disabled={context.disabled}
					aria-pressed={!allSides}
					onClick={() => setAllSides(false)}
				>
					<DisconnectOutlined /> Individual
				</button>
			</div>
			<div className={css.spaceFields} data-all-sides={allSides}>
				{allSides ? (
					<div className={css.spaceField}>
						<AssignmentCell
							{...shared}
							property={properties[0]}
							design={context.design}
							fonts={context.fonts}
							modeSelection={context.modeSelection}
							disabled={context.disabled}
							isBase={context.platform === 'base'}
							assignmentLabel="All sides"
							onChange={value => onChange(properties.map(property => ({ property, value })))}
						/>
					</div>
				) : (
					properties.map((property, index) => (
						<div className={css.spaceField} key={property}>
							<span>{['Top', 'Right', 'Bottom', 'Left'][index]}</span>
							<PlatformAssignment
								{...context}
								property={property}
								onChange={value => onChange([{ property, value }])}
							/>
						</div>
					))
				)}
			</div>
		</>
	);
}
